import React, { useState, useEffect, Fragment } from 'react';
import Button from '~~elements/Button';
import Select from '~~elements/Select';
import Modal from '~~elements/Modal';
import Input from '~~elements/Input';
import toast from '~~elements/Toast';
import Multilingual from '~~utils/Multilingual';
import { dateTimeFriendlyTransform, dateTimeStringPattern } from '~~utils/CommonUtils';
import { SeasonResource } from '~~apis/resource';
import { useTournamentDispatch, useTournamentState } from '../useTournament';
import { StyledForm } from '../styled';
import { gameStatus, gameFinishedStatus } from '../Const';
import { filter, find } from 'ramda';

const ScheduleCreateGameModal = ({
  tryCreateGame,
  createGameDefaultValue,
  handleCancelGameCreate,
  setSelectedGame,
}) => {
  const tournamentDispatch = useTournamentDispatch();
  const { tournamentInfo, games, teams, gameGroups } = useTournamentState();

  const [startEdit, setStartEdit] = useState(false);
  const [gameData, setGameData] = useState(JSON.parse(JSON.stringify(createGameDefaultValue)));

  const [apiInProcess, setApiInProcess] = useState(false);

  useEffect(() => {
    setStartEdit(false);
    setApiInProcess(false);
    setGameData(JSON.parse(JSON.stringify(createGameDefaultValue)));
  }, [createGameDefaultValue]);

  const handleInputOnChange = (e) => {
    setStartEdit(true);

    const name = e.target.name;
    const value = e.target.value;
    const indentedKeys = name.split('.');
    const indentedAmount = indentedKeys.length;

    let targetObject = { ...gameData };
    switch (indentedAmount) {
      case 3:
        targetObject = {
          ...gameData,
          [indentedKeys[0]]: {
            ...gameData[indentedKeys[0]],
            [indentedKeys[1]]: {
              ...gameData[indentedKeys[0]][indentedKeys[1]],
              [indentedKeys[2]]: value,
            },
          },
        };
        break;
      case 2:
        targetObject = {
          ...gameData,
          [indentedKeys[0]]: {
            ...gameData[indentedKeys[0]],
            [indentedKeys[1]]: value,
          },
        };
        break;
      default:
        targetObject = {
          ...gameData,
          [indentedKeys[0]]: value,
        };
        break;
    }

    setGameData(targetObject);
  };

  const handleCreateGame = (e) => {
    if (e.preventDefault) {
      e.preventDefault();
    }

    const errorToast = (msg) => {
      toast(msg, { status: 'error', second: 2 });
    };

    let seq = 0;
    if (!tournamentInfo.auto_game_seq) {
      seq = parseInt(gameData.seq);
      if (Number.isNaN(seq) || seq <= 0) {
        errorToast(Multilingual(`TOURNAMENT.ERROR.GAME_SEQ_FORMAT`));
        return;
      }

      if (find((g) => g.seq === seq && g.group === gameData.group, games)) {
        errorToast(Multilingual(`TOURNAMENT.ERROR.GAME_SEQ_DUPLICATE`));
        return;
      }
    }

    const dateTimeRegExp = new RegExp(`^${dateTimeStringPattern}$`);
    const scheduledStartAt = dateTimeFriendlyTransform(gameData.info.time.scheduled_start_at);
    if (!scheduledStartAt.match(dateTimeRegExp)) {
      errorToast(Multilingual(`TOURNAMENT.ERROR.GAME_DATE_TIME_HINT`));
      return;
    }

    let finishedStatus = '';
    let startedAt = '';
    let endedAt = '';
    let awayRuns = 0;
    let homeRuns = 0;
    if (gameData.info.status !== 'SCHEDULED') {
      if (gameData.away.team === 'NONE' || gameData.home.team === 'NONE') {
        errorToast(Multilingual(`TOURNAMENT.ERROR.GAME_NON_SCHEDULED_NO_TEAM`));
        return;
      }

      if (gameData.info.location_uniqid === 'NONE') {
        errorToast(Multilingual(`TOURNAMENT.ERROR.GAME_NON_SCHEDULED_NO_LOCATION`));
        return;
      }
    }
    if (gameData.info.status === 'FINISHED') {
      finishedStatus = gameData.info.finished_status;
      startedAt = dateTimeFriendlyTransform(gameData.info.time.started_at);
      endedAt = dateTimeFriendlyTransform(gameData.info.time.ended_at);
      if (!finishedStatus) {
        errorToast(Multilingual(`TOURNAMENT.ERROR.GAME_FINISHED_STATUS`));
        return;
      }
      if (!startedAt.match(dateTimeRegExp)) {
        errorToast(Multilingual(`TOURNAMENT.ERROR.GAME_DATE_TIME_HINT`));
        return;
      }
      if (!endedAt.match(dateTimeRegExp)) {
        errorToast(Multilingual(`TOURNAMENT.ERROR.GAME_DATE_TIME_HINT`));
        return;
      }

      awayRuns = parseInt(gameData.away.runs);
      if (Number.isNaN(awayRuns) || awayRuns < 0) {
        errorToast(Multilingual(`TOURNAMENT.ERROR.GAME_SCORE_FORMAT`));
        return;
      }
      homeRuns = parseInt(gameData.home.runs);
      if (Number.isNaN(homeRuns) || homeRuns < 0) {
        errorToast(Multilingual(`TOURNAMENT.ERROR.GAME_SCORE_FORMAT`));
        return;
      }
    }

    setApiInProcess(true);
    SeasonResource.createGame({
      seasonUniqid: tournamentInfo.uniqid,
      data: {
        seq,
        group: gameData.group,
        away: gameData.away.team,
        home: gameData.home.team,
        scheduled_start_at: scheduledStartAt,
        location_uniqid: gameData.info.location_uniqid,
        status: gameData.info.status,
        finished: {
          status: finishedStatus,
          started_at: startedAt,
          ended_at: endedAt,
          away_runs: awayRuns,
          home_runs: homeRuns,
        },
      },
    })
      .then(({ data: completedGame }) => {
        refetchGames().then(() => {
          toast(Multilingual(`SUCCESS.CREATE`), { status: 'success', second: 2 });
          setTimeout(() => {
            handleCancelGameCreate();
            setSelectedGame(completedGame.uniqid);
          }, 500);
        });
      })
      .catch((e) => {
        console.error(e);
        errorToast(Multilingual(`TOURNAMENT.ERROR.UNKNOWN`), { status: 'error' });
        setApiInProcess(false);
      });
  };

  const refetchGames = () => {
    return new Promise((resolve) => {
      SeasonResource.getGames({ seasonUniqid: tournamentInfo.uniqid })
        .then(({ data: newGames }) => {
          tournamentDispatch({ type: 'SET_GAMES', payload: newGames });
          resolve();
        })
        .catch((e) => {
          window.location.reload();
        });
    });
  };

  return (
    <Modal.Modal size="lg" isOpen={tryCreateGame} outterClickBack={!startEdit} onClickBack={handleCancelGameCreate}>
      <Modal.ModalHeader hasBack={!startEdit} onClickBack={handleCancelGameCreate}>
        {Multilingual(`TOURNAMENT.CREATE_GAME`)}
      </Modal.ModalHeader>
      <Modal.ModalBody>
        <StyledForm onSubmit={handleCreateGame}>
          <div className="box">
            <div className="input-field --multi-input">
              <div>
                {/* 客場球隊 */}
                <label>{Multilingual(`GAME.AWAY`)}</label>
                <Select width="100%" name="away.team" value={gameData.away.team} onChange={handleInputOnChange}>
                  <option value="NONE">{Multilingual(`GAME.TEAM_NONE`)}</option>
                  {teams.map((team) => (
                    <option key={team.name} value={team.name}>
                      {team.name}
                    </option>
                  ))}
                </Select>
              </div>
              <div>
                {/* 主場球隊 */}
                <label>{Multilingual(`GAME.HOME`)}</label>
                <Select width="100%" name="home.team" value={gameData.home.team} onChange={handleInputOnChange}>
                  <option value="NONE">{Multilingual(`GAME.TEAM_NONE`)}</option>
                  {teams.map((team) => (
                    <option key={team.name} value={team.name}>
                      {team.name}
                    </option>
                  ))}
                </Select>
              </div>
            </div>
            <div className="input-field --multi-input">
              {!tournamentInfo.auto_game_seq && (
                <div>
                  {/* 比賽編號 */}
                  <label>{Multilingual(`GAME.SEQ`)}</label>
                  <Input width="100%" name="seq" value={gameData.seq} onChange={handleInputOnChange} />
                </div>
              )}
              <div>
                {/* 比賽分類 */}
                <label>{Multilingual(`GAME.GROUP`)}</label>
                <Select width="100%" name="group" value={gameData.group} onChange={handleInputOnChange}>
                  <option value="NONE">{Multilingual(`GAME.GROUP_NONE`)}</option>
                  {gameGroups.map((group) => (
                    <option key={group.name} value={group.name}>
                      {group.name}
                    </option>
                  ))}
                </Select>
              </div>
              <div>
                {/* 預計開始時間 */}
                <label>{Multilingual(`GAME.SCHEDULED_START_AT`)}</label>
                <Input
                  width="100%"
                  name="info.time.scheduled_start_at"
                  placeholder={Multilingual(`TOURNAMENT.GAME_DATE_TIME_HINT`)}
                  value={gameData.info.time.scheduled_start_at}
                  onChange={handleInputOnChange}
                />
              </div>
              <div>
                {/* 地點 */}
                <label>{Multilingual(`GAME.LOCATION`)}</label>
                <Select
                  width="100%"
                  name="info.location_uniqid"
                  value={gameData.info.location_uniqid}
                  onChange={handleInputOnChange}
                >
                  <option value="NONE">{Multilingual(`GAME.LOCATION_NONE`)}</option>
                  {tournamentInfo.locations.map((location) => (
                    <option key={location.uniqid} value={location.uniqid}>
                      {`${location.city}｜${location.name}`}
                    </option>
                  ))}
                </Select>
              </div>
            </div>
            <div className="input-field --multi-input">
              <div>
                {/* 狀態 */}
                <label>{Multilingual(`GAME.STATUS.WORDING`)}</label>
                <Select width="100%" name="info.status" value={gameData.info.status} onChange={handleInputOnChange}>
                  {filter((s) => s !== 'PLAYING', gameStatus).map((status) => (
                    <option key={status} value={status}>
                      {Multilingual(`GAME.STATUS.${status}`)}
                    </option>
                  ))}
                </Select>
              </div>
              {gameData.info.status === 'FINISHED' && (
                <Fragment>
                  <div>
                    {/* 完賽狀態 */}
                    <label>{Multilingual(`GAME.FINISHED_STATUS.WORDING`)}</label>
                    <Select
                      width="100%"
                      name="info.finished_status"
                      value={gameData.info.finished_status}
                      onChange={handleInputOnChange}
                    >
                      <option value="">{Multilingual(`FORM.PLEASE_SELECT`)}</option>
                      {gameFinishedStatus.map((status) => (
                        <option key={status} value={status}>
                          {Multilingual(`GAME.FINISHED_STATUS.${status}`)}
                        </option>
                      ))}
                    </Select>
                  </div>
                  <div>
                    {/* 開始時間 */}
                    <label>{Multilingual(`GAME.STARTED_AT`)}</label>
                    <Input
                      width="100%"
                      name="info.time.started_at"
                      placeholder={Multilingual(`TOURNAMENT.GAME_DATE_TIME_HINT`)}
                      value={gameData.info.time.started_at}
                      onChange={handleInputOnChange}
                    />
                  </div>
                  <div>
                    {/* 結束時間 */}
                    <label>{Multilingual(`GAME.ENDED_AT`)}</label>
                    <Input
                      width="100%"
                      name="info.time.ended_at"
                      placeholder={Multilingual(`TOURNAMENT.GAME_DATE_TIME_HINT`)}
                      value={gameData.info.time.ended_at}
                      onChange={handleInputOnChange}
                    />
                  </div>
                </Fragment>
              )}
            </div>
            {gameData.info.status === 'FINISHED' && (
              <div className="input-field --multi-input">
                <div>
                  {/* 客場得分 */}
                  <label>{`${Multilingual(`GAME.AWAY`)} ${Multilingual(`GAME.SCORE`)}`}</label>
                  <Input
                    width="100%"
                    type="number"
                    name="away.runs"
                    value={gameData.away.runs}
                    min="0"
                    onChange={handleInputOnChange}
                  />
                </div>
                <div>
                  {/* 主場得分 */}
                  <label>{`${Multilingual(`GAME.HOME`)} ${Multilingual(`GAME.SCORE`)}`}</label>
                  <Input
                    width="100%"
                    type="number"
                    name="home.runs"
                    value={gameData.home.runs}
                    min="0"
                    onChange={handleInputOnChange}
                  />
                </div>
              </div>
            )}
          </div>
        </StyledForm>
      </Modal.ModalBody>
      <Modal.ModalFooter>
        {startEdit && (
          <Button color="red" width="unset" onClick={handleCreateGame} disabled={apiInProcess}>
            {Multilingual(`COMMON.CREATE`)}
          </Button>
        )}
        <Button color="transparentWithBorder" width="unset" onClick={handleCancelGameCreate} disabled={apiInProcess}>
          {Multilingual(`COMMON.CANCEL`)}
        </Button>
      </Modal.ModalFooter>
    </Modal.Modal>
  );
};

export default ScheduleCreateGameModal;
